// src/features/team/ScheduleFormDialog.tsx
// Create an on-call / rotation schedule — form-kit dialog (docs/ux/14-forms.md).

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { useTeam } from '@react/hooks';
import { FormDialog, TitleInput, AutoGrowTextarea, SegmentedControl, FormRow } from '@/components/form';
import { RolePicker } from './RolePicker';
import { scheduleTypeMap } from './logic';
import type { ScheduleType } from '@core/types/team';
import type { Entity } from '@core/types/entity';

const SCHEDULE_TYPE_OPTIONS = Object.entries(scheduleTypeMap).map(([value, labelKey]) => ({ value, labelKey }));

/**
 * An Entity reference carrying only what the caller knows. Platform, tenant and
 * product are left empty on purpose: the gateway has already established them
 * and the service fills them from the request context.
 */
function scopedEntity(id: string, typeName: string): Entity {
  return { id, type_id: typeName, type_name: typeName, platform_id: '', tenant_id: '', product_id: '' };
}

export interface ScheduleFormDialogProps {
  open: boolean;
  teamId: string;
  onClose: () => void;
  onCreated: () => void;
}

export function ScheduleFormDialog({ open, teamId, onClose, onCreated }: ScheduleFormDialogProps) {
  const { t } = useI18n();
  const store = useTeam();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scheduleType, setScheduleType] = useState<ScheduleType>('SCHEDULE_TYPE_ROTATION');
  const [roleId, setRoleId] = useState('');
  const [periodHours, setPeriodHours] = useState('24');
  const [timezone, setTimezone] = useState('UTC');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setDescription('');
    setScheduleType('SCHEDULE_TYPE_ROTATION');
    setRoleId('');
    setPeriodHours('24');
    setTimezone('UTC');
    setError(null);
  }, [open]);

  const dirty = !!(name.trim() || description.trim() || roleId);

  const submit = async () => {
    if (!name.trim() || !roleId || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // CreateRoleScheduleRequest is { schedule: RoleSchedule } — the fields
      // belong inside it, and the rotation period belongs inside `rotation`.
      // Sent flat (behind an `as never` that silenced the mismatch) every one
      // of them was dropped: the row landed with no name, no role and an
      // UNSPECIFIED type, and the list rendered it as "--".
      await store.CreateRoleSchedule(teamId, {
        schedule: {
          name: name.trim(),
          description: description.trim(),
          schedule_type: scheduleType,
          team: scopedEntity(teamId, 'team'),
          role: scopedEntity(roleId, 'role'),
          rotation: {
            period_hours: periodHours ? parseInt(periodHours, 10) : 24,
            timezone: timezone.trim() || 'UTC',
            days_of_week: [],
            start_time: '',
            end_time: '',
            notify_before_rotation: false,
            notify_minutes_before: 0,
            require_acknowledgment: false,
            handoff_overlap_minutes: 0,
          },
        },
      });
      onCreated();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('team.createSchedule')}
      canSubmit={!!name.trim() && !!roleId}
      isSubmitting={submitting}
      dirty={dirty}
      aria-label={t('team.createSchedule')}
    >
      <div className="space-y-1 px-3.5 pb-2 pt-2">
        {error && <p className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">{error}</p>}
        <TitleInput
          value={name}
          onChange={setName}
          placeholder={t('team.scheduleField.namePlaceholder')}
          initialFocus
          aria-label={t('common.name')}
        />
        <AutoGrowTextarea
          value={description}
          onChange={setDescription}
          placeholder={t('team.scheduleField.description')}
          minRows={1}
        />
      </div>

      <div className="px-3.5 pb-3">
        <SegmentedControl
          aria-label={t('team.scheduleField.type')}
          size="sm"
          value={scheduleType}
          onChange={(value) => setScheduleType(value as ScheduleType)}
          options={SCHEDULE_TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
        />
      </div>

      <div className="space-y-3 border-t border-border px-3.5 py-3">
        <FormRow label={t('common.role')} labelWidth="sm">
          <RolePicker teamId={teamId} value={roleId} onChange={setRoleId} className="max-w-xs" />
        </FormRow>
        <FormRow label={t('common.period')} labelWidth="sm" hint="hours">
          <input
            type="text"
            inputMode="numeric"
            value={periodHours}
            onChange={(e) => { if (e.target.value === '' || /^\d+$/.test(e.target.value)) setPeriodHours(e.target.value); }}
            placeholder="24"
            className="w-24 rounded-md border border-input bg-background px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <FormRow label={t('team.settings.timezone')} labelWidth="sm">
          <input
            type="text"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="UTC"
            className="w-32 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
      </div>
    </FormDialog>
  );
}

export default ScheduleFormDialog;
